import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, Sparkles, Loader2, Zap } from "lucide-react";
import { api, streamSSE } from "@/lib/api";
import { RichText } from "./RichText";
import { aiCue } from "@/lib/cues";

const ACTION_LABEL = {
  create_loan_tasks: "Loan tasks ready", open_wire_ops: "Opening wire dashboard", attach_samples: "Sample documents attached",
  extract: "Facts extracted & ratios calculated", generate_memo: "Drafting credit memo", filter: "Filter applied",
  clear_filter: "Filter cleared", set_jurisdiction: "Jurisdiction switched", run_gap_analysis: "Gap analysis drafted (pending review)", open_alert: "Opening alert", unsupported: "Could not interpret", unknown_loan: "Unknown property", denied: "Blocked by access control",
  set_fact: "Figure saved", set_fact_failed: "Figure not saved",
};

function ActionChips({ actions }) {
  const nav = useNavigate();
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {actions.filter((a) => a.action !== "answer").map((a, i) => (
        <div key={i} data-testid={`chat-action-${a.action}`} className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1.5 ${["unsupported", "unknown_loan", "denied", "set_fact_failed"].includes(a.action) ? "border-amber-500/40 text-amber-700 bg-amber-500/5" : "border-sky-500/30 text-sky-700 bg-sky-600/5"}`}>
          <Zap className="w-3 h-3" />{ACTION_LABEL[a.action] || a.action}{a.action === "set_fact" && a.key && <span className="num">: {a.key.replace(/_/g, " ")} → {typeof a.saved_value === "number" ? a.saved_value.toLocaleString("en-US") : a.saved_value}</span>}
          {a.tasks?.map((t) => (
            <button key={t.id} data-testid={`chat-open-task-${t.id}`} onClick={() => nav(`/task/${t.id}`)} className="ml-1 underline underline-offset-2 hover:text-sky-900">{t.title}</button>
          ))}
        </div>
      ))}
    </div>
  );
}

function Message({ m, metrics }) {
  if (m.role === "user")
    return <div className="rise ml-8 bg-slate-100 border border-slate-300/60 rounded-md px-3 py-2 text-sm text-slate-900">{m.content}</div>;
  return (
    <div className="rise">
      <div className="flex items-center gap-1.5 eyebrow mb-1.5"><Sparkles className="w-3 h-3 text-sky-600" />OmniFi AI</div>
      {m.actions?.length > 0 && <ActionChips actions={m.actions} />}
      {m.status && !m.content && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" />{m.status}…</div>}
      <RichText text={m.content} metrics={metrics} />
    </div>
  );
}

export function ChatPanel({ taskId, title, suggestions = [], metrics, onAction, onDone }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get(`/tasks/${taskId}/messages`).then((r) => setMsgs(r.data)); }, [taskId]);
  useEffect(() => {
    const h = (e) => { if (e.detail.taskId === taskId) send(e.detail.text); };
    window.addEventListener("cloudscore:chat", h);
    return () => window.removeEventListener("cloudscore:chat", h);
  });
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const patchLast = (fn) => setMsgs((ms) => [...ms.slice(0, -1), fn(ms[ms.length - 1])]);
  const send = async (q) => {
    const t = (q ?? text).trim();
    if (!t || busy) return;
    setText(""); setBusy(true);
    setMsgs((ms) => [...ms, { role: "user", content: t }, { role: "assistant", content: "", actions: [], status: "Thinking" }]);
    const cue = aiCue(t);
    try {
      await streamSSE(`/tasks/${taskId}/chat`, { text: t }, (ev) => {
        if (ev.type === "status") patchLast((m) => ({ ...m, status: ev.content }));
        if (ev.type === "action") { patchLast((m) => ({ ...m, actions: [...m.actions, ev] })); onAction?.(ev); }
        if (ev.type === "delta") { cue.delta(ev.content); patchLast((m) => ({ ...m, content: m.content + ev.content })); }
      });
    } catch (e) {
      patchLast((m) => ({ ...m, content: "Connection interrupted. Please retry." }));
    }
    cue.done();
    patchLast((m) => ({ ...m, status: null }));
    setBusy(false);
    onDone?.();
  };

  return (
    <div data-testid="chat-panel" className="flex flex-col h-full min-h-0 bg-slate-50">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div><div className="eyebrow">Conversation</div><div className="text-sm text-slate-800 font-medium truncate">{title}</div></div>
        <span className="text-[10px] num text-slate-500">Claude Sonnet 4.5</span>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll px-4 py-4 space-y-5" data-testid="chat-messages">
        {msgs.map((m, i) => <Message key={m.id || i} m={m} metrics={metrics} />)}
        <div ref={end} />
      </div>
      <div className="p-3 border-t border-slate-200 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((s, i) => (
            <button key={s} data-testid={`chat-suggestion-${i}`} disabled={busy} onClick={() => send(s)}
              className="text-[11px] text-left px-2 py-1 rounded border border-slate-300 text-slate-600 hover:text-slate-900 hover:border-sky-500/50 transition-colors disabled:opacity-40">{s}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 bg-white border border-slate-300 focus-within:border-sky-500/60 rounded-md p-2 transition-colors">
          <textarea data-testid="chat-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask in plain English…" className="flex-1 bg-transparent resize-none outline-none text-sm text-slate-900 placeholder:text-slate-400" />
          <button data-testid="chat-send-button" type="submit" disabled={busy || !text.trim()}
            className="h-8 w-8 rounded bg-sky-600 text-white flex items-center justify-center hover:bg-sky-700 transition-colors disabled:opacity-30">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}
